import type { ProjectPersonaAwareness } from "schemas/generated/projectContext";
import type { SightglassCreativeAssetCopy } from "schemas/generated/sightglass";
import type {
  SightglassAdLaunchWindow,
  SightglassAdListResult,
  SightglassAdvertiserWire,
  SightglassAdWire,
  SightglassAnglesWire,
  SightglassBoardItem,
  SightglassBoardListResult,
  SightglassBoardWire,
  SightglassCalendarWire,
  SightglassCategoryLeadersWire,
  SightglassCategoryShareWire,
  SightglassCategoryWire,
  SightglassCompanyFilterOptionsWire,
  SightglassCompanyLaunchesWire,
  SightglassCompanyListResult,
  SightglassCompanyPersonasWire,
  SightglassCompanyTechStackWire,
  SightglassCompanyTrendWire,
  SightglassCompanyWire,
  SightglassDaysActiveBucketsWire,
  SightglassFilterOptionsWire,
  SightglassGroupedShareWire,
  SightglassHomeSummaryWire,
  SightglassListKind,
  SightglassListPersonasWire,
  SightglassMarketPulseWire,
  SightglassPageVersionsWire,
  SightglassPersonaPlanWire,
  SightglassProjectCompanyWire,
  SightglassSavedSearchListResult,
  SightglassSavedSearchWire,
  SightglassSavedSummaryWire,
  SightglassShareGroupBy,
  SightglassSitePageListResult,
  SightglassSitePageWire,
  SightglassTechnologyDetailWire,
  SightglassTechnologyListResult,
  SightglassTechnologyRankWire,
  SightglassTopAnglesSort,
  SightglassTopOffersSort,
  SightglassTopOffersWire,
  SightglassTopShareWire,
} from "schemas/generated/sightglassApi";
import type {
  SightglassArenaMapBody,
  SightglassArenaMapWire,
  SightglassArenaPoles,
} from "schemas/generated/sightglassArena";
import type {
  SightglassAgeBand,
  SightglassAngleType,
  SightglassFunnelStage,
  SightglassHookType,
  SightglassOfferTag,
  SightglassPageType as SightglassReadingPageType,
} from "schemas/sightglassOffers";
import type {
  SightglassAccessWire,
  SightglassPlan,
} from "schemas/sightglassPlans";
import type { RequestFn } from "./request";

import {
  SIGHTGLASS_PLAN_OVERRIDE_HEADER,
  SIGHTGLASS_PROJECT_HEADER,
} from "schemas/sightglassPlans";

// eslint-disable-next-line replo/no-export-from -- SDK public API surface for Sightglass result types
export type {
  SightglassAdListResult,
  SightglassAdvertiserWire,
  SightglassBoardItem,
  SightglassAdWire,
  SightglassCategoryLeadersWire,
  SightglassCategoryShareWire,
  SightglassCategoryWire,
  SightglassCompanyLaunchesWire,
  SightglassCompanyListResult,
  SightglassCompanyTrendWire,
  SightglassCompanyWire,
  SightglassDaysActiveBucketsWire,
  SightglassFilterOptionsWire,
  SightglassGroupedShareWire,
  SightglassMarketPulseWire,
  SightglassCompanyFilterOptionsWire,
  SightglassShareGroupBy,
  SightglassTopShareWire,
  SightglassHomeSummaryWire,
  SightglassSitePageListResult,
  SightglassSitePageWire,
  SightglassTechnologyDetailWire,
  SightglassTechnologyListResult,
  SightglassTechnologyRankWire,
  SightglassCalendarWire,
  SightglassCampaignWire,
  SightglassCampaignAdWire,
  SightglassCampaignPageWire,
  SightglassCampaignCompanyWire,
  SightglassTopOffersWire,
  SightglassTopOffersSort,
  SightglassPageVersionsWire,
  SightglassPageVersionWire,
  SightglassAnglesWire,
  SightglassAngleWire,
  SightglassTopAnglesSort,
  SightglassCompanyPersonasWire,
  SightglassPersonaWire,
  SightglassPersonaLaneWire,
  SightglassPersonaPlanWire,
  SightglassAdTagsWire,
  SightglassCompanyMix,
  SightglassListKind,
  SightglassListPersonaWire,
  SightglassListPersonasWire,
  SightglassProjectCompanyWire,
} from "schemas/generated/sightglassApi";
// eslint-disable-next-line replo/no-export-from -- SDK public API surface for the Arena map
export type {
  SightglassArenaItemWire,
  SightglassArenaMapWire,
  SightglassArenaPoles,
} from "schemas/generated/sightglassArena";
// eslint-disable-next-line replo/no-export-from -- SDK public API surface for Sightglass angle filters
export type { SightglassAngleType } from "schemas/sightglassOffers";

type SightglassDisplayFormat =
  | "image"
  | "video"
  | "carousel"
  | "dco"
  | "dpa"
  | "text"
  | "other";

/** The reading's page types; the stored names product, squeeze and other still filter. */
type SightglassPageType =
  | SightglassReadingPageType
  | "product"
  | "squeeze"
  | "other";

type SightglassScoreTier =
  | "testing"
  | "scaling"
  | "growing"
  | "optimized"
  | "winning";

/** Multi-value filters are arrays here and CSV on the wire, so callers never string-join. */
export type SightglassAdsFindInput = {
  companyIds?: string[];
  categoryIds?: string[];
  search?: string;
  /** Defaults to hybrid: text matches first, semantic matches filling the rest. */
  searchMode?: "semantic" | "text" | "hybrid";
  advertiserId?: string;
  sitePageId?: string;
  displayFormats?: SightglassDisplayFormat[];
  publisherPlatforms?: string[];
  languages?: string[];
  pageTypes?: SightglassPageType[];
  hookTypes?: SightglassHookType[];
  angleTypes?: SightglassAngleType[];
  offerTags?: SightglassOfferTag[];
  funnelStages?: SightglassFunnelStage[];
  /** Only ads on this ad list of the caller's. */
  listId?: string;
  /** Only ads of the companies on this company list of the caller's. */
  companyListId?: string;
  technologies?: string[];
  domains?: string[];
  status?: "active" | "inactive";
  activityWindow?: "90d";
  launchWindow?: SightglassAdLaunchWindow;
  daysActiveGte?: number;
  daysActiveLte?: number;
  duplicationCountGte?: number;
  scoreTiers?: SightglassScoreTier[];
  adDeliveryStartAtGte?: string;
  adDeliveryStartAtLt?: string;
  creativeId?: string;
  /** At most this many ads per advertiser; 0 or omitted is uncapped. */
  adsPerAdvertiserLimit?: number;
  /** Fold every placement of one creative into a single row. */
  collapseVariants?: boolean;
  /** Only meaningful on `saved.findAds`. */
  boardId?: string;
  sort?: "newest" | "longest_running" | "performance" | "most_duplicated";
  cursor?: string;
  limit?: number;
};

export type SightglassCompaniesFindInput = {
  search?: string;
  searchMode?: "hybrid" | "text" | "semantic";
  categoryIds?: string[];
  similarToCompanyId?: string;
  activeAdsCountGte?: number;
  platforms?: string[];
  countryCodes?: string[];
  plans?: string[];
  momentumLabels?: string[];
  /** Technology display names; a company matches when it carries any of them. */
  technologies?: string[];
  sort?:
    | "most_active_ads"
    | "ad_growth_7d"
    | "ad_growth_30d"
    | "most_launched"
    | "most_launched_7d"
    | "most_top_grade_ads"
    | "most_estimated_sales"
    | "most_visits"
    | "most_total_ads"
    | "momentum"
    | "newest"
    | "name";
  /** Reverses the sort's natural direction; rows with no value stay at the tail. */
  sortDirection?: "asc" | "desc";
  cursor?: string;
  limit?: number;
};

export type SightglassTechnologiesFindInput = {
  search?: string;
  category?: string;
  sort?:
    | "most_companies"
    | "top_100_share"
    | "adopter_momentum"
    | "adopter_ad_growth"
    | "name";
  limit?: number;
};

export type SightglassSitePagesFindInput = {
  categoryIds?: string[];
  /** Only meaningful on `saved.findSitePages`. */
  boardId?: string;
  search?: string;
  searchMode?: "semantic" | "text";
  domains?: string[];
  pageTypes?: SightglassPageType[];
  technologies?: string[];
  activeAdsCountGte?: number;
  /** Only pages of the companies on this company list of the caller's. */
  companyListId?: string;
  sort?: "newest" | "most_ads";
  /** At most 50 pages spread round-robin across the companies in scope; no next page. */
  balance?: boolean;
  cursor?: string;
  limit?: number;
};

function boardItemPath({
  boardId,
  item,
}: {
  boardId: string;
  item: SightglassBoardItem;
}): string {
  const base = `/api/v1/sightglass/boards/${encodeURIComponent(boardId)}`;
  if ("adId" in item) {
    return `${base}/ads/${encodeURIComponent(item.adId)}`;
  }
  if ("sitePageId" in item) {
    return `${base}/site-pages/${encodeURIComponent(item.sitePageId)}`;
  }
  if ("personaId" in item) {
    return `${base}/personas/${encodeURIComponent(item.personaId)}`;
  }
  return `${base}/companies/${encodeURIComponent(item.companyId)}`;
}

function toCsvQuery(
  input: Record<string, unknown>,
): Record<string, string | number | boolean | undefined> {
  const query: Record<string, string | number | boolean | undefined> = {};
  for (const [key, value] of Object.entries(input)) {
    if (value === undefined || value === null) {
      continue;
    }
    if (Array.isArray(value)) {
      if (value.length > 0) {
        query[key] = value.join(",");
      }
      continue;
    }
    if (
      typeof value === "string" ||
      typeof value === "number" ||
      typeof value === "boolean"
    ) {
      query[key] = value;
    }
  }
  return query;
}

export function createSightglassClient({
  request,
  getProjectId,
}: {
  request: RequestFn;
  /** The project the caller is working in; the server bills Sightglass access to its workspace. */
  getProjectId: () => string | null;
}) {
  let planOverride: SightglassPlan | null = null;
  const send: RequestFn = (options) => {
    const projectId = getProjectId();
    return request({
      ...options,
      headers: {
        ...options.headers,
        ...(projectId ? { [SIGHTGLASS_PROJECT_HEADER]: projectId } : {}),
        ...(planOverride
          ? { [SIGHTGLASS_PLAN_OVERRIDE_HEADER]: planOverride }
          : {}),
      },
    });
  };
  return {
    /** Staff QA only: the server honours it for superusers and ignores it for everyone else. */
    setPlanOverride: (plan: SightglassPlan | null) => {
      planOverride = plan;
    },
    access: {
      /** What the caller's plan sees, and this month's research usage. */
      get: (): Promise<SightglassAccessWire> => {
        return send<SightglassAccessWire>({
          method: "GET",
          path: "/api/v1/sightglass/access",
        });
      },
    },
    ads: {
      find: (
        input: SightglassAdsFindInput,
      ): Promise<SightglassAdListResult> => {
        return send<SightglassAdListResult>({
          method: "GET",
          path: "/api/v1/sightglass/ads",
          query: toCsvQuery(input),
        });
      },
      get: (input: { adId: string }): Promise<SightglassAdWire> => {
        return send<SightglassAdWire>({
          method: "GET",
          path: `/api/v1/sightglass/ads/${encodeURIComponent(input.adId)}`,
        });
      },
    },
    offers: {
      /** Offer campaigns for the caller's saved brands, or for `companyRefs`. Dates are YYYY-MM-DD. */
      calendar: (input: {
        from?: string;
        to?: string;
        companyRefs?: string[];
      }): Promise<SightglassCalendarWire> => {
        return send<SightglassCalendarWire>({
          method: "GET",
          path: "/api/v1/sightglass/calendar",
          query: {
            from: input.from,
            to: input.to,
            companyRefs: input.companyRefs?.join(","),
          },
        });
      },
      /** One brand's campaigns. */
      company: (input: {
        companyRef: string;
        from?: string;
        to?: string;
      }): Promise<SightglassCalendarWire> => {
        return send<SightglassCalendarWire>({
          method: "GET",
          path: `/api/v1/sightglass/companies/${encodeURIComponent(input.companyRef)}/campaigns`,
          query: { from: input.from, to: input.to },
        });
      },
      /** The Arena's offer leaderboard. */
      top: (input: {
        days?: number;
        sort?: SightglassTopOffersSort;
        limit?: number;
      }): Promise<SightglassTopOffersWire> => {
        return send<SightglassTopOffersWire>({
          method: "GET",
          path: "/api/v1/sightglass/offers/top",
          query: { days: input.days, sort: input.sort, limit: input.limit },
        });
      },
      /** A landing page's dated captures, newest first. */
      pageVersions: (input: {
        sitePageId: string;
      }): Promise<SightglassPageVersionsWire> => {
        return send<SightglassPageVersionsWire>({
          method: "GET",
          path: `/api/v1/sightglass/site-pages/${encodeURIComponent(input.sitePageId)}/versions`,
        });
      },
    },
    angles: {
      /** The Arena's angle leaderboard: the pitch each brand's ads lean on, ranked over the window. */
      top: (input: {
        days?: number;
        sort?: SightglassTopAnglesSort;
        type?: SightglassAngleType;
        ageBand?: SightglassAgeBand;
        awareness?: ProjectPersonaAwareness;
        limit?: number;
      }): Promise<SightglassAnglesWire> => {
        return send<SightglassAnglesWire>({
          method: "GET",
          path: "/api/v1/sightglass/angles/top",
          query: {
            days: input.days,
            sort: input.sort,
            type: input.type,
            ageBand: input.ageBand,
            awareness: input.awareness,
            limit: input.limit,
          },
        });
      },
      /** Every angle one brand uses, most used first. */
      company: (input: {
        companyRef: string;
      }): Promise<SightglassAnglesWire> => {
        return send<SightglassAnglesWire>({
          method: "GET",
          path: `/api/v1/sightglass/companies/${encodeURIComponent(input.companyRef)}/angles`,
        });
      },
    },
    personas: {
      /** One brand's customer personas, with the angles, offers, ads and pages aimed at each. */
      company: (input: {
        companyRef: string;
      }): Promise<SightglassCompanyPersonasWire> => {
        return send<SightglassCompanyPersonasWire>({
          method: "GET",
          path: `/api/v1/sightglass/companies/${encodeURIComponent(input.companyRef)}/personas`,
        });
      },
      /** The planner: the project's personas against the saved (or given) brands' personas. */
      plan: (input: {
        projectId?: string;
        companyRefs?: string[];
      }): Promise<SightglassPersonaPlanWire> => {
        return send<SightglassPersonaPlanWire>({
          method: "GET",
          path: "/api/v1/sightglass/personas/plan",
          query: {
            projectId: input.projectId,
            companyRefs: input.companyRefs?.join(","),
          },
        });
      },
    },
    technologies: {
      find: (
        input: SightglassTechnologiesFindInput,
      ): Promise<SightglassTechnologyListResult> => {
        return send<SightglassTechnologyListResult>({
          method: "GET",
          path: "/api/v1/sightglass/technologies",
          query: toCsvQuery(input),
        });
      },
      get: (input: {
        slug: string;
      }): Promise<SightglassTechnologyDetailWire> => {
        return send<SightglassTechnologyDetailWire>({
          method: "GET",
          path: `/api/v1/sightglass/technologies/${encodeURIComponent(input.slug)}`,
        });
      },
    },
    companies: {
      find: (
        input: SightglassCompaniesFindInput,
      ): Promise<SightglassCompanyListResult> => {
        return send<SightglassCompanyListResult>({
          method: "GET",
          path: "/api/v1/sightglass/companies",
          query: toCsvQuery(input),
        });
      },
      get: (input: { companyRef: string }): Promise<SightglassCompanyWire> => {
        return send<SightglassCompanyWire>({
          method: "GET",
          path: `/api/v1/sightglass/companies/${encodeURIComponent(input.companyRef)}`,
        });
      },
      techStack: (input: {
        companyRef: string;
      }): Promise<SightglassCompanyTechStackWire> => {
        return send<SightglassCompanyTechStackWire>({
          method: "GET",
          path: `/api/v1/sightglass/companies/${encodeURIComponent(input.companyRef)}/tech-stack`,
        });
      },
      trend: (input: {
        companyRef: string;
        days?: number;
      }): Promise<SightglassCompanyTrendWire> => {
        return send<SightglassCompanyTrendWire>({
          method: "GET",
          path: `/api/v1/sightglass/companies/${encodeURIComponent(input.companyRef)}/trend`,
          query: input.days ? { days: String(input.days) } : {},
        });
      },
      launches: (input: {
        companyRef: string;
        weeks?: number;
      }): Promise<SightglassCompanyLaunchesWire> => {
        return send<SightglassCompanyLaunchesWire>({
          method: "GET",
          path: `/api/v1/sightglass/companies/${encodeURIComponent(input.companyRef)}/launches`,
          query: input.weeks ? { weeks: String(input.weeks) } : {},
        });
      },
      daysActive: (input: {
        companyRef: string;
      }): Promise<SightglassDaysActiveBucketsWire> => {
        return send<SightglassDaysActiveBucketsWire>({
          method: "GET",
          path: `/api/v1/sightglass/companies/${encodeURIComponent(input.companyRef)}/days-active`,
        });
      },
      similar: (input: {
        companyRef: string;
        limit?: number;
      }): Promise<SightglassCompanyListResult> => {
        return send<SightglassCompanyListResult>({
          method: "GET",
          path: `/api/v1/sightglass/companies/${encodeURIComponent(input.companyRef)}/similar`,
          query: input.limit ? { limit: String(input.limit) } : {},
        });
      },
      topShare: (input: {
        weeks?: number;
        limit?: number;
        categoryId?: string;
      }): Promise<SightglassTopShareWire> => {
        return send<SightglassTopShareWire>({
          method: "GET",
          path: "/api/v1/sightglass/companies/top-share",
          query: toCsvQuery(input),
        });
      },
      groupedShare: (input: {
        groupBy?: SightglassShareGroupBy;
        weeks?: number;
        limit?: number;
        categoryId?: string;
      }): Promise<SightglassGroupedShareWire> => {
        return send<SightglassGroupedShareWire>({
          method: "GET",
          path: "/api/v1/sightglass/companies/grouped-share",
          query: toCsvQuery(input),
        });
      },
      pulse: (input: {
        categoryId?: string;
      }): Promise<SightglassMarketPulseWire> => {
        return send<SightglassMarketPulseWire>({
          method: "GET",
          path: "/api/v1/sightglass/companies/pulse",
          query: toCsvQuery(input),
        });
      },
      filterOptions: (input: {
        categoryId?: string;
      }): Promise<SightglassCompanyFilterOptionsWire> => {
        return send<SightglassCompanyFilterOptionsWire>({
          method: "GET",
          path: "/api/v1/sightglass/companies/filter-options",
          query: toCsvQuery(input),
        });
      },
      categoryShare: (input: {
        days?: 7 | 30;
      }): Promise<SightglassCategoryShareWire> => {
        return send<SightglassCategoryShareWire>({
          method: "GET",
          path: "/api/v1/sightglass/companies/category-share",
          query: toCsvQuery(input),
        });
      },
      categoryLeaders: (input: {
        categoryId: string;
        limit?: number;
      }): Promise<SightglassCategoryLeadersWire> => {
        return send<SightglassCategoryLeadersWire>({
          method: "GET",
          path: "/api/v1/sightglass/companies/category-leaders",
          query: toCsvQuery(input),
        });
      },
      /** Add by domain: tracked, saved for the caller, and researched now. */
      create: (input: { domain: string }): Promise<SightglassCompanyWire> => {
        return send<SightglassCompanyWire>({
          method: "POST",
          path: "/api/v1/sightglass/companies",
          body: input,
        });
      },
    },
    categories: {
      find: (input?: {
        parentId?: string;
      }): Promise<{ items: SightglassCategoryWire[] }> => {
        return send<{ items: SightglassCategoryWire[] }>({
          method: "GET",
          path: "/api/v1/sightglass/categories",
          query: toCsvQuery(input ?? {}),
        });
      },
    },
    sitePages: {
      find: (
        input: SightglassSitePagesFindInput,
      ): Promise<SightglassSitePageListResult> => {
        return send<SightglassSitePageListResult>({
          method: "GET",
          path: "/api/v1/sightglass/site-pages",
          query: toCsvQuery(input),
        });
      },
      get: (input: { sitePageId: string }): Promise<SightglassSitePageWire> => {
        return send<SightglassSitePageWire>({
          method: "GET",
          path: `/api/v1/sightglass/site-pages/${encodeURIComponent(input.sitePageId)}`,
        });
      },
      /** Superusers only: cut the page into sections and add them to the inspiration registry. */
      addToInspiration: (input: {
        sitePageId: string;
      }): Promise<{
        status: "started" | "already-running";
        instanceId: string;
      }> => {
        return send({
          method: "POST",
          path: `/api/v1/sightglass/site-pages/${encodeURIComponent(input.sitePageId)}/inspiration`,
        });
      },
      removeFromInspiration: (input: {
        sitePageId: string;
      }): Promise<{ removed: number }> => {
        return send({
          method: "DELETE",
          path: `/api/v1/sightglass/site-pages/${encodeURIComponent(input.sitePageId)}/inspiration`,
        });
      },
      /** Superuser curation: takes the page out of every listing. Reversible from Replo Admin. */
      hide: (input: { sitePageId: string }): Promise<{ hidden: true }> => {
        return send({
          method: "POST",
          path: `/api/v1/sightglass/site-pages/${encodeURIComponent(input.sitePageId)}/hide`,
        });
      },
    },
    filterOptions: {
      get: (): Promise<SightglassFilterOptionsWire> => {
        return send<SightglassFilterOptionsWire>({
          method: "GET",
          path: "/api/v1/sightglass/filter-options",
        });
      },
    },
    creatives: {
      copyToAsset: (input: {
        creativeId: string;
        projectId: string;
      }): Promise<SightglassCreativeAssetCopy> => {
        return send<SightglassCreativeAssetCopy>({
          method: "POST",
          path: `/api/v1/sightglass/creatives/${encodeURIComponent(input.creativeId)}/copy-to-asset`,
          body: { projectId: input.projectId },
        });
      },
    },
    boards: {
      find: (): Promise<SightglassBoardListResult> => {
        return send<SightglassBoardListResult>({
          method: "GET",
          path: "/api/v1/sightglass/boards",
        });
      },
      /** A list holds one kind; adding another kind to it is a 400. */
      create: (input: {
        name: string;
        kind: SightglassListKind;
      }): Promise<SightglassBoardWire> => {
        return send<SightglassBoardWire>({
          method: "POST",
          path: "/api/v1/sightglass/boards",
          body: input,
        });
      },
      update: (input: {
        boardId: string;
        name: string;
      }): Promise<SightglassBoardWire> => {
        return send<SightglassBoardWire>({
          method: "PATCH",
          path: `/api/v1/sightglass/boards/${encodeURIComponent(input.boardId)}`,
          body: { name: input.name },
        });
      },
      delete: (input: { boardId: string }): Promise<{ deleted: true }> => {
        return send<{ deleted: true }>({
          method: "DELETE",
          path: `/api/v1/sightglass/boards/${encodeURIComponent(input.boardId)}`,
        });
      },
      addItem: (input: {
        boardId: string;
        item: SightglassBoardItem;
      }): Promise<{ added: true }> => {
        return send<{ added: true }>({
          method: "PUT",
          path: boardItemPath(input),
        });
      },
      removeItem: (input: {
        boardId: string;
        item: SightglassBoardItem;
      }): Promise<{ removed: true }> => {
        return send<{ removed: true }>({
          method: "DELETE",
          path: boardItemPath(input),
        });
      },
    },
    /** The user's default board, plus everything on any board of theirs. */
    saved: {
      add: (item: SightglassBoardItem): Promise<{ boardId: string }> => {
        return send<{ boardId: string }>({
          method: "POST",
          path: "/api/v1/sightglass/saved",
          body: item,
        });
      },
      remove: (item: SightglassBoardItem): Promise<{ deleted: true }> => {
        return send<{ deleted: true }>({
          method: "DELETE",
          path: "/api/v1/sightglass/saved",
          body: item,
        });
      },
      findAds: (
        input: SightglassAdsFindInput = {},
      ): Promise<SightglassAdListResult> => {
        return send<SightglassAdListResult>({
          method: "GET",
          path: "/api/v1/sightglass/saved/ads",
          query: toCsvQuery(input),
        });
      },
      findSitePages: (
        input: SightglassSitePagesFindInput = {},
      ): Promise<SightglassSitePageListResult> => {
        return send<SightglassSitePageListResult>({
          method: "GET",
          path: "/api/v1/sightglass/saved/site-pages",
          query: toCsvQuery(input),
        });
      },
      findCompanies: (
        input: { boardId?: string } = {},
      ): Promise<SightglassCompanyListResult> => {
        return send<SightglassCompanyListResult>({
          method: "GET",
          path: "/api/v1/sightglass/saved/companies",
          query: toCsvQuery(input),
        });
      },
      findPersonas: (
        input: { boardId?: string } = {},
      ): Promise<SightglassListPersonasWire> => {
        return send<SightglassListPersonasWire>({
          method: "GET",
          path: "/api/v1/sightglass/saved/personas",
          query: toCsvQuery(input),
        });
      },
      /** Counts plus a few thumbnails — what the chrome on every route needs. */
      getSummary: (): Promise<SightglassSavedSummaryWire> => {
        return send<SightglassSavedSummaryWire>({
          method: "GET",
          path: "/api/v1/sightglass/saved/summary",
        });
      },
    },
    projectCompany: {
      /** The company the project is linked to ("your company"); `company` is null until it is. */
      get: (input: {
        projectId: string;
      }): Promise<SightglassProjectCompanyWire> => {
        return send<SightglassProjectCompanyWire>({
          method: "GET",
          path: "/api/v1/sightglass/project-company",
          query: { projectId: input.projectId },
        });
      },
    },
    arena: {
      /** A custom axis from one word: its opposite and three phrases per end. */
      axis: (input: { low: string }): Promise<SightglassArenaPoles> => {
        return send<SightglassArenaPoles>({
          method: "POST",
          path: "/api/v1/sightglass/arena/axis",
          body: input,
        });
      },
      /** Items placed on two axes; scope is a company list, else the top companies. */
      map: (input: SightglassArenaMapBody): Promise<SightglassArenaMapWire> => {
        return send<SightglassArenaMapWire>({
          method: "POST",
          path: "/api/v1/sightglass/arena/map",
          body: input,
        });
      },
    },
    home: {
      /** Everything the Home view shows, in one round trip. */
      getSummary: (): Promise<SightglassHomeSummaryWire> => {
        return send<SightglassHomeSummaryWire>({
          method: "GET",
          path: "/api/v1/sightglass/home-summary",
        });
      },
    },
    savedSearches: {
      find: (): Promise<SightglassSavedSearchListResult> => {
        return send<SightglassSavedSearchListResult>({
          method: "GET",
          path: "/api/v1/sightglass/saved-searches",
        });
      },
      create: (input: {
        name: string;
        query: Record<string, unknown>;
      }): Promise<SightglassSavedSearchWire> => {
        return send<SightglassSavedSearchWire>({
          method: "POST",
          path: "/api/v1/sightglass/saved-searches",
          body: input,
        });
      },
      delete: (input: {
        savedSearchId: string;
      }): Promise<{ deleted: true }> => {
        return send<{ deleted: true }>({
          method: "DELETE",
          path: `/api/v1/sightglass/saved-searches/${encodeURIComponent(input.savedSearchId)}`,
        });
      },
    },
  };
}

export type SightglassClient = ReturnType<typeof createSightglassClient>;
